/**
 * Campuzon Campus Representative Network.
 *
 * Representatives are authorised field operators, not promoters. Every entity
 * here exists so an operational action can be traced: who did it, where, with
 * what evidence, under which rule, approved by whom.
 */

export type RepRank =
  | 'Campus Representative'
  | 'Senior Representative'
  | 'Area Lead'
  | 'Campus Lead'
  | 'Regional Lead'
  | 'National Campus Lead'

export const RANK_ORDER: RepRank[] = [
  'Campus Representative',
  'Senior Representative',
  'Area Lead',
  'Campus Lead',
  'Regional Lead',
  'National Campus Lead',
]

export type RepStatus =
  | 'Applicant'
  | 'Pending onboarding'
  | 'Active'
  | 'Probation'
  | 'Inactive'
  | 'Suspended'
  | 'Terminated'
  | 'Alumni'

export type ApplicationStatus =
  | 'Draft'
  | 'Submitted'
  | 'Under review'
  | 'Shortlisted'
  | 'Interview'
  | 'Approved'
  | 'Rejected'
  | 'Waitlisted'
  | 'Withdrawn'

/** A region groups campuses. Administrators create these; nothing is hard-coded. */
export interface Region {
  id: string
  name: string
  country: string
  currency: string
  leadRepId: string | null
}

export interface CampusArea {
  id: string
  name: string
  /** Accommodation zone, market strip, transport corridor, and so on. */
  kind: string
  areaLeadRepId: string | null
  minutesFromGate: number
}

/**
 * Where a campus is in its rollout. This is campus configuration, never copy:
 * the public network page and the application form both read it, so no campus
 * can imply operational presence it does not have.
 */
export type CampusRecruitmentStatus =
  | 'Preparation'
  | 'Recruiting'
  | 'Pilot'
  | 'Active'
  | 'Paused'
  | 'Closed'
  | 'Not yet supported'

/** Whether the campus accepts applications right now, and what to tell applicants. */
export const CAMPUS_INTAKE: Record<
  CampusRecruitmentStatus,
  { open: boolean; held: boolean; note: string }
> = {
  Preparation: {
    open: true,
    held: true,
    note: 'Being prepared. Applications are held until the campus opens.',
  },
  Recruiting: { open: true, held: false, note: 'Recruiting now. Applications are reviewed as they arrive.' },
  Pilot: { open: true, held: false, note: 'Running a pilot team. A small number of places are open.' },
  Active: { open: true, held: false, note: 'Operating. Places open as the campus needs them.' },
  Paused: { open: false, held: true, note: 'Intake paused. Existing representatives continue working.' },
  Closed: { open: false, held: false, note: 'Not accepting applications.' },
  'Not yet supported': {
    open: true,
    held: true,
    note: 'Not yet configured. Applications are held and used to decide where we open next.',
  },
}

export interface NetworkCampus {
  id: string
  name: string
  institution: string
  institutionType: string
  regionId: string
  city: string
  state: string
  country: string
  coordinates: { lat: number; lng: number }
  studentEstimate: number
  active: boolean
  published: boolean
  recruitmentStatus: CampusRecruitmentStatus
  campusLeadRepId: string | null
  areas: CampusArea[]
  transportNote: string
  website: string
}

/** A real uploaded file, with its own review lifecycle. */
export type DocumentReviewState =
  | 'Not provided'
  | 'Submitted'
  | 'Under review'
  | 'Verified'
  | 'Rejected'
  | 'Re-upload required'

export interface ApplicationDocument {
  key: string
  label: string
  hint: string
  required: boolean
  fileName: string | null
  sizeBytes: number | null
  mimeType: string | null
  /** Object URL for preview. Production stores the file in secure object storage. */
  previewUrl: string | null
  state: DocumentReviewState
  reviewNote: string | null
}

export type ApplicantStatus = 'Current student' | 'Recent graduate' | 'Other'

export interface RepApplication {
  id: string
  submittedOn: string | null
  status: ApplicationStatus
  fullName: string
  email: string
  phone: string
  institution: string
  campusId: string
  /** Recorded at submission so a later status change cannot rewrite history. */
  campusStatusAtApplication?: CampusRecruitmentStatus
  applicantStatus: ApplicantStatus
  programme: string
  level: string
  graduationYear: string
  /** Structured, not a free-text address. No exact home address at this stage. */
  location: { area: string; city: string; state: string }
  residentialArea: string
  weeklyHours: string
  availableDays: string[]
  fieldPeriods: string[]
  transport: string
  hasSmartphone: boolean
  hasCamera: boolean
  connectivity: string
  experience: string
  motivation: string
  /** Areas of interest — not an entitlement to perform them. */
  preferredActivities: string[]
  consents: { key: string; label: string; given: boolean }[]
  documents: ApplicationDocument[]
  internalNotes: { at: string; actor: string; body: string }[]
  /** Set on approval. */
  assignedCampusId?: string
  assignedAreaId?: string
  assignedRank?: RepRank
  supervisorRepId?: string
  decisionReason?: string
}

export interface OnboardingStep {
  key: string
  label: string
  detail: string
  required: boolean
  done: boolean
}

export interface Representative {
  id: string
  repCode: string
  name: string
  initials: string
  campusId: string
  areaId: string | null
  rank: RepRank
  status: RepStatus
  supervisorRepId: string | null
  joinedOn: string
  referralCode: string
  onboarding: OnboardingStep[]
  trainingCompleted: string[]
  warnings: number
  /** Counters are derived from records elsewhere; kept for alumni history only. */
  historic: { verified: number; referred: number; businesses: number }
}

export type TaskType =
  | 'Verify property'
  | 'Reverify property'
  | 'Verify business'
  | 'Onboard landlord'
  | 'Onboard business'
  | 'Collect campus intelligence'
  | 'Confirm availability'
  | 'Investigate report'
  | 'Collect student feedback'
  | 'Support campus event'

export type TaskStatus =
  | 'Open'
  | 'Assigned'
  | 'Accepted'
  | 'In progress'
  | 'Submitted'
  | 'Under review'
  | 'Rework required'
  | 'Approved'
  | 'Rejected'
  | 'Expired'

export interface EvidenceRequirement {
  key: string
  label: string
  kind: 'gps' | 'photo' | 'video' | 'field' | 'declaration'
  required: boolean
}

export interface FieldTask {
  id: string
  type: TaskType
  title: string
  detail: string
  campusId: string
  areaId: string | null
  priority: 'Routine' | 'Priority' | 'Urgent'
  status: TaskStatus
  assignedRepId: string | null
  createdBy: string
  createdOn: string
  dueOn: string
  rewardRuleId: string
  /** Rank required to claim. Restricted task types are not self-claimable. */
  minRank: RepRank
  requiresTraining: string[]
  evidence: EvidenceRequirement[]
  propertyRef?: string
  expectedLocation?: { lat: number; lng: number }
  reviewerId?: string
  reviewNotes?: string
  history: { at: string; actor: string; label: string }[]
}

export interface CapturedEvidence {
  gps: { lat: number; lng: number; accuracyM: number; at: string } | null
  photos: { key: string; label: string; at: string; dataUrl: string }[]
  declaration: boolean
  fields: Record<string, string>
}

export type VerificationDecision = 'Approved' | 'Rejected' | 'Rework required'

export interface VerificationSubmission {
  id: string
  taskId: string
  repId: string
  propertyRef: string
  submittedOn: string
  status: 'Submitted' | 'Under review' | VerificationDecision | 'Expired'
  evidence: CapturedEvidence
  distanceFromExpectedM: number | null
  flags: string[]
  reviewerId?: string
  reviewNotes?: string
  decidedOn?: string
  /** Set only when a reviewer approves. Drives the Campuzon Verified badge. */
  verifiedUntil?: string
  syncState: 'Synced' | 'Queued offline' | 'Syncing' | 'Sync failed'
}

export interface RewardRule {
  id: string
  label: string
  activity: string
  kind: 'Fixed' | 'Commission' | 'Bonus' | 'Referral'
  amount: number
  currency: string
  /** Percentage rules use this instead of a flat amount. */
  percentage?: number
  campusId: string | null
  requiresApproval: boolean
  effectiveFrom: string
  effectiveTo: string | null
  monthlyCap: number | null
  active: boolean
}

export type EarningStatus =
  | 'Pending'
  | 'Under review'
  | 'Approved'
  | 'Rejected'
  | 'Paid'
  | 'Reversed'
  | 'Disputed'

export interface EarningEntry {
  id: string
  repId: string
  ruleId: string
  activity: string
  sourceRef: string
  grossAmount: number
  adjustment: number
  currency: string
  status: EarningStatus
  createdOn: string
  approvedOn: string | null
  approvedBy: string | null
  paidOn: string | null
  /** Reversals reference the entry they correct; originals are never edited. */
  reversalOf?: string
  reason?: string
}

export interface Referral {
  id: string
  repId: string
  code: string
  stage: 'Click' | 'Registered' | 'Verified account' | 'First action' | 'Transacted'
  createdOn: string
  studentLabel: string
  attributed: boolean
  flag: string | null
}

export interface Lead {
  id: string
  kind: 'Landlord' | 'Property manager' | 'Agent' | 'Hostel' | 'Business'
  name: string
  contactMasked: string
  campusId: string
  areaId: string | null
  repId: string
  stage: 'Created' | 'Contacted' | 'Interested' | 'Onboarding' | 'Verified' | 'Active' | 'Lost'
  category?: string
  notes: string
  followUpOn: string
  createdOn: string
}

export type IntelCategory =
  | 'Accommodation'
  | 'Transport'
  | 'Food'
  | 'Retail'
  | 'Internet'
  | 'Security'
  | 'Utilities'
  | 'Prices'
  | 'Local developments'
  | 'Student needs'

export interface IntelReport {
  id: string
  campusId: string
  areaId: string | null
  category: IntelCategory
  observation: string
  repId: string
  observedOn: string
  confidence: 'Low' | 'Medium' | 'High'
  status: 'Submitted' | 'Under review' | 'Verified' | 'Published' | 'Rejected' | 'Expired'
  expiresOn: string | null
  hasEvidence: boolean
}

export interface TrainingModule {
  id: string
  title: string
  detail: string
  minutes: number
  required: boolean
  unlocks: TaskType[]
  quiz: { question: string; options: string[]; answerIndex: number }[]
}

export interface PerformanceEvent {
  id: string
  repId: string
  at: string
  kind:
    | 'Verification approved'
    | 'Verification reworked'
    | 'Verification rejected'
    | 'Task completed late'
    | 'Referral converted'
    | 'Policy warning'
    | 'Evidence quality'
  weightNote: string
  delta: number
}

export interface Incident {
  id: string
  repId: string
  campusId: string
  kind: string
  detail: string
  reportedOn: string
  confidential: boolean
  status: 'Open' | 'Escalated' | 'Resolved'
  handledBy: string | null
}

export interface FraudFlag {
  id: string
  repId: string
  signal: string
  detail: string
  severity: 'Low' | 'Medium' | 'High'
  raisedOn: string
  state: 'Awaiting review' | 'Cleared' | 'Upheld'
}

export interface Dispute {
  id: string
  repId: string
  about: 'Rejected task' | 'Rejected verification' | 'Reward' | 'Performance score' | 'Suspension'
  ref: string
  argument: string
  raisedOn: string
  state: 'Open' | 'Under review' | 'Upheld' | 'Declined'
  decision: string | null
}

export interface AuditEntry {
  id: string
  at: string
  actor: string
  action: string
  entity: string
  from: string | null
  to: string | null
  reason: string | null
}

export type FeatureFlagKey =
  | 'representative_network'
  | 'property_verification'
  | 'business_onboarding'
  | 'campus_intelligence'
  | 'referral_rewards'
  | 'payouts'
  | 'leadership'
  | 'offline_mode'
  | 'campus_map'

export type FeatureFlags = Record<FeatureFlagKey, boolean>
